import { useCallback, useEffect, useState } from 'react';
import { formatCents, type BidInput, type BidResult, type PricingConfig } from '@lbhl/shared';
import {
  ApiError,
  OfflineError,
  fetchMe,
  fetchPublishedPricing,
  getToken,
  setToken,
  type SessionUser,
} from './lib/api.js';
import {
  allBids,
  cacheIdentity,
  cachePricing,
  putBid,
  readCachedIdentity,
  readCachedPricing,
  removeLocalBid,
  storedPrices,
  type StoredBid,
} from './lib/db.js';
import { runSync, startSyncWatchers, subscribeSync, type SyncState } from './lib/sync.js';
import { BidForm, type BidMeta } from './components/BidForm.js';
import { Admin } from './components/Admin.js';
import { Users } from './components/Users.js';
import { AllBids } from './components/AllBids.js';
import { ConfirmDialog } from './components/ConfirmDialog.js';
import { OverflowMenu } from './components/OverflowMenu.js';
import { describeDeviceRemoval } from './lib/removal.js';
import { decideBoot, type ServerResult } from './lib/boot.js';
import { SignIn } from './components/SignIn.js';

type Boot =
  | { kind: 'loading' }
  | { kind: 'login'; message?: string }
  | {
      kind: 'ready';
      user: SessionUser;
      pricing: PricingConfig;
      pricingFetchedAt: string;
      /** Running on cached pricing because the server was unreachable. */
      stale: boolean;
      /** The session is gone, so bids will queue rather than sync. */
      sessionExpired: boolean;
    }
  | { kind: 'blocked'; message: string };

export function App() {
  const [boot, setBoot] = useState<Boot>({ kind: 'loading' });
  /**
   * Set when the user asks to sign in. Kept as state rather than done with a
   * reload, because a reload plus a cleared token used to land straight back on
   * the form -- making sign-in unreachable and queued bids unsyncable.
   */
  const [loginRequested, setLoginRequested] = useState(false);
  const [sync, setSync] = useState<SyncState>({ kind: 'idle', pending: 0 });
  const [tab, setTab] = useState<'new' | 'saved' | 'admin' | 'users' | 'allbids'>('new');
  const [bids, setBids] = useState<StoredBid[]>([]);

  const reloadBids = useCallback(async () => setBids(await allBids()), []);

  /** After publishing, pull the new live pricing so this device prices with it. */
  const reloadPricing = useCallback(async () => {
    const pricing = await fetchPublishedPricing();
    await cachePricing(pricing);
    setBoot((prev) =>
      prev.kind === 'ready'
        ? { ...prev, pricing, pricingFetchedAt: new Date().toISOString(), stale: false }
        : prev,
    );
  }, []);

  useEffect(() => subscribeSync(setSync), []);
  useEffect(() => startSyncWatchers(), []);
  useEffect(() => void reloadBids(), [reloadBids]);

  const runBoot = useCallback(async (userRequestedLogin: boolean) => {
    const [cached, cachedMe] = await Promise.all([readCachedPricing(), readCachedIdentity()]);
    const hasToken = getToken() !== null;

    let serverResult: ServerResult = 'not-attempted';
    let fresh: { user: SessionUser; pricing: PricingConfig } | null = null;
    let errorMessage: string | null = null;

    // Only talk to the server when there is a session to talk with, and when the
    // user is not explicitly asking for the sign-in screen.
    if (hasToken && !userRequestedLogin) {
      try {
        const [user, pricing] = await Promise.all([fetchMe(), fetchPublishedPricing()]);
        fresh = { user, pricing };
        serverResult = 'ok';
        await Promise.all([cachePricing(pricing), cacheIdentity(user)]);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) serverResult = 'unauthorized';
        else if (err instanceof OfflineError) serverResult = 'offline';
        else {
          serverResult = 'error';
          errorMessage = (err as Error).message;
        }
      }
    }

    const decision = decideBoot({
      hasToken,
      hasCachedPricing: cached !== undefined,
      userRequestedLogin,
      serverResult,
    });

    switch (decision.kind) {
      case 'login':
        setBoot({
          kind: 'login',
          ...(decision.reason === 'expired'
            ? { message: 'Your session expired. Sign in again to sync.' }
            : {}),
        });
        return;

      case 'blocked':
        setBoot({
          kind: 'blocked',
          message:
            decision.reason === 'offline-no-cache'
              ? 'No connection, and no pricing has been cached on this device yet. Connect once to set up.'
              : (errorMessage ?? 'Could not load pricing.'),
        });
        return;

      case 'ready': {
        if (decision.source === 'server' && fresh) {
          setBoot({
            kind: 'ready',
            user: fresh.user,
            pricing: fresh.pricing,
            pricingFetchedAt: new Date().toISOString(),
            stale: false,
            sessionExpired: false,
          });
          return;
        }
        setBoot({
          kind: 'ready',
          user: cachedMe
            ? { id: cachedMe.id, email: cachedMe.email, name: cachedMe.name, role: cachedMe.role }
            : { id: 0, email: 'offline', name: 'Offline', role: 'estimator' },
          pricing: cached!.config,
          pricingFetchedAt: cached!.fetchedAt,
          stale: true,
          sessionExpired: decision.sessionExpired,
        });
      }
    }
  }, []);

  useEffect(() => {
    void runBoot(loginRequested);
  }, [runBoot, loginRequested]);

  const handleSave = useCallback(
    async (input: BidInput, result: BidResult, meta: BidMeta) => {
      const bid: StoredBid = {
        // Minted here, on the device. This is what makes the sync retry-safe.
        id: crypto.randomUUID(),
        pricingVersionId: Number(result.pricingVersionId),
        engineVersion: result.engineVersion,
        input,
        result,
        status: 'final',
        customer: meta.customer,
        property: meta.property,
        createdAt: new Date().toISOString(),
        sync: 'pending',
      };

      // Local first, always. Saving must not depend on a network or a session.
      await putBid(bid);
      await reloadBids();
      void runSync().then(reloadBids);
    },
    [reloadBids],
  );

  if (boot.kind === 'loading') return <div className="center">Loading…</div>;

  if (boot.kind === 'blocked') {
    return (
      <div className="wrap">
        <div className="note bad" style={{ marginTop: 40 }}>{boot.message}</div>
      </div>
    );
  }

  if (boot.kind === 'login') {
    return (
      <SignIn
        message={boot.message}
        // Without this, asking to sign in while offline is a dead end: an
        // installed PWA has no address bar or reload button to escape with.
        onCancel={loginRequested ? () => setLoginRequested(false) : undefined}
        onSignedIn={async () => {
          setLoginRequested(false);
          await runBoot(false);
          // Drain whatever was waiting on a session.
          void runSync().then(reloadBids);
        }}
      />
    );
  }

  return (
    <>
      <header className="app">
        <div className="wrap">
          <div className="row">
            <h1>LBHL Bid Tool</h1>
            <SyncBadge state={sync} />
          </div>
          <nav className="tabs">
            <button aria-selected={tab === 'new'} onClick={() => setTab('new')}>
              New bid
            </button>
            <button aria-selected={tab === 'saved'} onClick={() => { setTab('saved'); void reloadBids(); }}>
              Saved{bids.length > 0 ? ` (${bids.length})` : ''}
            </button>
            {boot.user.role === 'admin' && (
              <>
                <button aria-selected={tab === 'admin'} onClick={() => setTab('admin')}>
                  Pricing
                </button>
                <button aria-selected={tab === 'allbids'} onClick={() => setTab('allbids')}>
                  All bids
                </button>
                <button aria-selected={tab === 'users'} onClick={() => setTab('users')}>
                  People
                </button>
              </>
            )}
          </nav>
        </div>
      </header>

      <div className="wrap">
        {boot.stale && (
          <div className="note warn">
            Working offline from pricing cached{' '}
            {new Date(boot.pricingFetchedAt).toLocaleString()}. Bids are saved on this device and
            will sync when you reconnect.
          </div>
        )}
        {(sync.kind === 'needs-login' || boot.sessionExpired) && (
          <div className="note warn">
            You are signed out, so new bids will be saved on this device and queued
            {sync.pending > 0 ? ` (${sync.pending} waiting)` : ''}. Nothing is lost — you can keep
            working.{' '}
            <button className="ghost" onClick={() => setLoginRequested(true)}>
              Sign in
            </button>
          </div>
        )}

        {tab === 'new' && <BidForm pricing={boot.pricing} user={boot.user} onSave={handleSave} />}
        {tab === 'saved' && (
          <SavedBids
            bids={bids}
            onRetry={() => void runSync().then(reloadBids)}
            onRemove={async (id) => {
              await removeLocalBid(id);
              await reloadBids();
            }}
          />
        )}
        {tab === 'admin' && boot.user.role === 'admin' && (
          boot.stale ? (
            <div className="note warn">
              Pricing administration needs a connection — drafting and publishing a version is a
              server operation.
            </div>
          ) : (
            <Admin onPublished={() => void reloadPricing()} />
          )
        )}
        {tab === 'allbids' && boot.user.role === 'admin' && (
          boot.stale ? (
            <div className="note warn">
              Reviewing and deleting bids needs a connection — it works on the server's records,
              not this device's.
            </div>
          ) : (
            <AllBids />
          )
        )}
        {tab === 'users' && boot.user.role === 'admin' && (
          boot.stale ? (
            <div className="note warn">
              Managing people needs a connection.
            </div>
          ) : (
            <Users currentUserId={boot.user.id} />
          )
        )}
      </div>
    </>
  );
}

function SyncBadge({ state }: { state: SyncState }) {
  switch (state.kind) {
    case 'syncing':
      return <span className="badge">Syncing {state.pending}…</span>;
    case 'offline':
      return <span className="badge warn">Offline · {state.pending} queued</span>;
    case 'needs-login':
      return <span className="badge bad">Sign-in needed</span>;
    case 'error':
      return <span className="badge bad">Sync error</span>;
    default:
      return state.pending > 0
        ? <span className="badge warn">{state.pending} queued</span>
        : <span className="badge ok">Synced</span>;
  }
}

function SavedBids({
  bids,
  onRetry,
  onRemove,
}: {
  bids: StoredBid[];
  onRetry(): void;
  onRemove(id: string): Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);
  /** The bid awaiting confirmation. One dialog serves the whole list. */
  const [pending, setPending] = useState<StoredBid | null>(null);
  const [busy, setBusy] = useState(false);

  if (bids.length === 0) {
    return <div className="center">No bids on this device yet.</div>;
  }

  return (
    <section className="card">
      <h2>
        Bids on this device
        <button className="ghost" style={{ float: 'right', marginTop: -6 }} onClick={onRetry}>
          Sync now
        </button>
      </h2>
      {error && <div className="note bad">{error}</div>}

      {bids.map((b) => (
        <div className="bidrow" key={b.id}>
          <span className="who">
            <b>{b.customer.name || 'Unnamed customer'}</b>
            <span>
              {b.property.addressLine1 || 'no address'} ·{' '}
              {new Date(b.createdAt).toLocaleDateString()}
            </span>
            {b.sync === 'pending' && <span className="badge warn">Queued</span>}
            {b.sync === 'blocked' && <span className="badge bad">Rejected: {b.syncError}</span>}
            {b.needsReview && <span className="badge warn">Flagged for review</span>}
          </span>
          {/* Every package's price, since the bid quotes all of them and the
              customer has not chosen. Read through storedPrices because a bid this
              device saved on an older bundle has one total, not three, and reaching
              straight for `quotes` would take the whole list down. */}
          {(() => {
            const { legacy, amounts } = storedPrices(b);
            return (
              <span className="amt amts">
                {amounts.map((cents, i) => (
                  <span key={i}>{formatCents(cents)}</span>
                ))}
                {legacy && <span className="cap">older form</span>}
              </span>
            );
          })()}

          {/* A queued bid is the only copy of work the office has never seen, so it
              gets no removal control at all rather than one that refuses. */}
          {b.sync !== 'pending' && (
            <OverflowMenu
              label={`Actions for the bid for ${b.customer.name || 'an unnamed customer'}`}
              items={[
                {
                  label: 'Remove from this device…',
                  danger: true,
                  disabled: busy,
                  onSelect: () => {
                    setError(null);
                    setPending(b);
                  },
                },
              ]}
            />
          )}
        </div>
      ))}

      <div className="note">
        Removing here clears the bid from this device only. Anything synced stays on the
        server, where an admin can see it.
      </div>

      {pending && (
        <ConfirmDialog
          copy={describeDeviceRemoval({
            sync: pending.sync,
            customerName: pending.customer.name,
          })}
          busy={busy}
          onCancel={() => setPending(null)}
          onConfirm={() => {
            setBusy(true);
            void onRemove(pending.id)
              .catch((err) => setError((err as Error).message))
              .finally(() => {
                setBusy(false);
                setPending(null);
              });
          }}
        />
      )}
    </section>
  );
}

